import type {
  AdBlastReportCompany,
  AdBlastReportLine,
} from "~/ui-rtk/api/adBlastReportsApi"
import useDate from "~/ui-rtk/hooks/date"
import {
  type CustomerGoalStatus,
  formatPercent,
  isLaunched,
  isLive,
} from "./aggregate"
import {
  AdNameRef,
  CompareValue,
  ExternalLink,
  KpiRow,
  MetaStrip,
  Person,
  PostRef,
  SectionBand,
  SheetTable,
  StatusPill,
  typeLabel,
} from "./sheet"

/** Tracker columns, in the sheet's order — shared with the CSV export. */
export const TRACKER_HEADERS = [
  "Launch Date",
  "Status",
  "Created Via",
  "Created By",
  "Campaign // Ad Sets Launched",
  "Ad Name",
  "Landing Page",
  "Post ID",
  "Preview (link)",
  "Channel",
  "Type",
  "Notes",
] as const

export const trackerRow = (
  line: AdBlastReportLine,
  formatDate: (iso: string) => string,
): unknown[] => [
  formatDate(line.launchedAt),
  line.status,
  line.createdVia,
  line.createdBy,
  line.campaignAdSets,
  line.adName,
  line.landingPage,
  line.postId,
  line.previewUrl,
  line.channel,
  typeLabel(line.type),
  line.notes,
]

/**
 * One company's sheet — the same shape as a customer tab of the manual
 * launch tracker: a goal strip on top, then one row per launched ad in the
 * selected range with its Campaign//AdSet target.
 */
export default function CompanySheet({
  company,
  lines,
  goalStatus,
  range,
  comparison,
}: {
  company: AdBlastReportCompany
  /** This company's lines inside the selected range, newest first. */
  lines: AdBlastReportLine[]
  goalStatus?: CustomerGoalStatus
  range: { from: Date; to: Date }
  /** This company's lines inside the comparison period, when one is set. */
  comparison?: { from: Date; to: Date; lines: AdBlastReportLine[] }
}) {
  const dayjs = useDate()
  const fmtDateTime = (d: Date | string) => dayjs(d).format("YYYY-MM-DD HH:mm")
  const launched = lines.filter(isLaunched).length
  const live = lines.filter(isLive).length
  const prev = comparison
    ? {
        launched: comparison.lines.filter(isLaunched).length,
        live: comparison.lines.filter(isLive).length,
      }
    : null
  return (
    <div className="flex flex-col">
      <div className="rounded-t-lg border border-system-blue-600/40 bg-system-blue-700 px-3 py-2 text-[18px] font-bold text-white">
        Marathon Engine Ads Launch Tracker - {company.name}
      </div>
      <MetaStrip
        items={[
          {
            label: "Range",
            value: `${fmtDateTime(range.from)} → ${fmtDateTime(range.to)}`,
          },
          {
            label: "Weekly goal",
            value: goalStatus?.hasGoal
              ? `${goalStatus.goalLow} – ${goalStatus.goalHigh}`
              : "not set",
          },
          {
            label: "This week",
            value: goalStatus
              ? `${goalStatus.thisWeek.live} live · ${goalStatus.goalStatus} · ${formatPercent(goalStatus.attainment)}`
              : "—",
          },
          ...(comparison
            ? [
                {
                  label: "Compared to",
                  value: `${fmtDateTime(comparison.from)} → ${fmtDateTime(comparison.to)}`,
                },
              ]
            : []),
        ]}
      />

      <SectionBand title="Range summary" />
      <KpiRow
        items={[
          {
            label: "Ads launched",
            value: (
              <CompareValue
                value={launched}
                previous={prev?.launched}
                align="center"
              />
            ),
          },
          {
            label: "Ads live",
            value: (
              <CompareValue value={live} previous={prev?.live} align="center" />
            ),
          },
          {
            label: "Channels",
            value:
              [...new Set(lines.map(l => l.channel).filter(Boolean))].join(
                ", ",
              ) || "—",
          },
        ]}
      />

      <SectionBand title="Launches" />
      <SheetTable
        rows={lines}
        rowKey={l => l.id}
        dense
        maxHeight="max-h-[70vh]"
        emptyLabel="No Marathon blasts for this customer in the selected range."
        columns={[
          {
            header: "Launch Date",
            render: l => fmtDateTime(l.launchedAt),
            align: "right",
            className: "whitespace-nowrap",
          },
          { header: "Status", render: l => <StatusPill status={l.status} /> },
          {
            header: "Created Via",
            render: l => l.createdVia,
            className: "whitespace-nowrap",
          },
          {
            header: "Created By",
            render: l => <Person value={l.createdBy} />,
            className: "min-w-[180px] max-w-[260px] break-words",
          },
          {
            header: "Campaign // Ad Sets Launched",
            render: l => (
              <ul className="space-y-0.5">
                {l.adSets.map((s, i) => (
                  <li
                    key={`${s.adSetId}:${i}`}
                    className={s.success ? undefined : "text-rose-300"}
                  >
                    {s.campaignName ?? "?"}//{s.adSetName ?? s.adSetId}
                    {s.adStatus && (
                      <span className="ml-1 text-[10px] uppercase text-system-gray-500">
                        {s.adStatus}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ),
            className: "min-w-[300px] max-w-[460px] break-words",
          },
          {
            header: "Ad Name",
            render: l => (
              <AdNameRef name={l.adName} previewUrl={l.previewUrl} />
            ),
            className: "min-w-[220px] max-w-[340px] break-words",
          },
          {
            header: "Landing Page",
            render: l => (
              <ExternalLink
                href={l.landingPage}
                label={l.landingPage.replace(/^https?:\/\//, "")}
              />
            ),
            className: "min-w-[280px] max-w-[440px] break-all",
          },
          { header: "Post ID", render: l => <PostRef postId={l.postId} /> },
          {
            header: "Preview",
            render: l => (
              <ExternalLink href={l.previewUrl} label="Ads Manager" />
            ),
          },
          { header: "Channel", render: l => l.channel },
          { header: "Type", render: l => typeLabel(l.type) },
          {
            header: "Notes",
            render: l => l.notes,
            className:
              "min-w-[240px] max-w-[380px] break-words text-[11px] text-system-gray-400",
          },
        ]}
      />
    </div>
  )
}
